/**
 * Dashboard — central layout & routing logic
 *
 * 💡 To add a new route:
 * - Add it to routes.ts
 * - Register it in dashboardRoutes.ts
 * - Add to sidebar in UserData.tsx
 *
 * See Dashboard/README.md for full guide.
 */
import { useState, useEffect, useMemo, useCallback } from "react"
import { useQuery } from "react-query"
import { Navigate, Route, Routes, useLocation, useNavigate } from "react-router-dom"

import Fade from "@mui/material/Fade"

import moment from "moment"

import { VisionAPI } from "../../API/VisionAPI"
import Chatbot from "../../components/Chatbot/Chatbot"
import { browserHistoryListener } from "../../history"
import { routes } from "../../routes/routes"
import { useLicenseStore } from "../../store"
import { useFeaturesStore } from "../../store/featuresStore"
import { Persona } from "../../types/Custom/Types"
import { definitions } from "../../types/Generated/apiTypes"
import Auth from "../../utils/auth"
import SubscriptionEnded from "../StatusScreens/SubscriptionEnded/SubscriptionEnded"
import ExpiryWarning from "./components/ExpiryWarning"
import LoadingScreen from "./components/LoadingScreen"
import Sidebar from "./components/Sidebar"
import TopBar from "./components/TopBar"
import { getFeatureRouteElement } from "./components/dashboardRoutes"
import { getDashboardRoutes } from "./components/dashboardRoutes"
import { getFirstAuthorizedAndActiveScreen } from "./helpers"

import styles from "./Dashboard.module.scss"

type Features = definitions["Features"]

/**
 * Represents the available personas and their access status in the application
 */
type AvailablePersonas = {
  hasAnalytics: boolean
  hasAdmin: boolean
  hasMarketing: boolean
  hasSecurity: boolean
  hasIndustrial: boolean
}

/**
 * Main Dashboard component that handles the application's main layout and routing
 * @returns {JSX.Element} The rendered Dashboard component
 */
const Dashboard = (): JSX.Element => {
  const navigate = useNavigate()
  const location = useLocation()

  const [landingScreen, setLandingScreen] = useState<string>()
  const [isDrawerOpen, setIsDrawerOpen] = useState(false)
  const [persona, setPersona] = useState<Persona>("analytics")
  const { isWarningDisplayed = false, licenseInfo, setLicenseInfo, setIsWarningDisplayed } = useLicenseStore()
  const { setFeatures, features } = useFeaturesStore()

  /**
   * Fetches license information and handles license-related state updates
   */
  const { isLoading: isLoadingLicense } = useQuery("fetchLicenseInfo", () => VisionAPI.fetchSynapseLicenseInfo(), {
    onSuccess: (data) => {
      setLicenseInfo(data)
      const daysLeft = moment(data.valid_until).diff(moment(), "days", true)
      setIsWarningDisplayed(daysLeft < 30)
      // Set active modules based on license features
      setFeatures(data.features)
    },
  })

  /**
   * Memoized permissions array from localStorage
   */
  const permissions = useMemo(() => JSON.parse(localStorage.getItem("permissions") || "[]"), [])

  /**
   * Memoized object containing available personas based on user permissions and features
   * @returns {AvailablePersonas} Object containing boolean flags for each persona's availability
   */
  const availablePersonas = useMemo((): AvailablePersonas => {
    if (isLoadingLicense || !features) {
      return {
        hasAnalytics: false,
        hasAdmin: false,
        hasMarketing: false,
        hasSecurity: false,
        hasIndustrial: false,
      }
    }
    const normalize = (s: string) => s.replace(/_/g, " ")
    const hasPermission = (persona: string) => permissions.some((p: string) => normalize(p).includes(persona))
    const hasActiveFeatures = (persona: keyof Features) => {
      const group = features[persona]
      return group ? Object.entries(group).some(([key, val]) => key !== "azka_bot" && !!val) : false
    }
    return {
      hasAnalytics: hasPermission("analytics") && hasActiveFeatures("analytics"),
      hasAdmin: hasPermission("admin"),
      hasMarketing: hasPermission("marketing") && hasActiveFeatures("marketing"),
      hasSecurity: hasPermission("security") && hasActiveFeatures("security"),
      hasIndustrial: hasPermission("industrial") && hasActiveFeatures("industrial"),
    }
  }, [permissions, features, isLoadingLicense])

  /**
   * Effect to handle browser history changes
   */
  useEffect(() => browserHistoryListener(location), [location])

  /**
   * Effect to handle initial persona selection and navigation
   */
  useEffect(() => {
    if (!isLoadingLicense) {
      const personaPriority: (keyof AvailablePersonas)[] = [
        "hasAnalytics",
        "hasAdmin",
        "hasMarketing",
        "hasSecurity",
        "hasIndustrial",
      ]

      const personaKey = personaPriority.find((key) => availablePersonas?.[key])
      if (personaKey) {
        const persona = personaKey.replace("has", "").toLowerCase() as
          | "analytics"
          | "admin"
          | "marketing"
          | "security"
          | "industrial"
        setLandingScreen(getFirstAuthorizedAndActiveScreen({ persona }))
      } else {
        navigate(`/${routes.noPermissions}`)
      }
    }
  }, [availablePersonas, navigate, isLoadingLicense])

  // called when user click on one of he user available personas
  // to update active persona and navigate to first authorized screen in that persona
  const togglePersonas = (persona: Persona) => {
    const firstAuthScreen = getFirstAuthorizedAndActiveScreen({ persona })
    navigate(firstAuthScreen)
  }

  /**
   * Toggles the drawer open/closed state
   */
  const handleToggleDrawer = () => setIsDrawerOpen((prev) => !prev)

  /**
   * Closes the drawer
   */
  const handleCloseDrawer = useCallback(() => setIsDrawerOpen(false), [])

  /**
   * Determines if navigation elements should be hidden based on current route
   */
  const shouldHideNavigation =
    location.pathname === `/${routes?.[404]}` || location.pathname === `/${routes?.noPermissions}`

  /**
   * Memoized dashboard routes based on features and landing screen
   */
  const dashboardRoutes = useMemo(() => getDashboardRoutes(features ?? null, landingScreen), [features, landingScreen])

  if (isLoadingLicense) return <LoadingScreen isLoading />

  if (!licenseInfo || moment(licenseInfo?.valid_until).isBefore(moment(), "day")) {
    return (
      <Fade in timeout={1500}>
        <div>
          <Routes>
            <Route path="*" element={<Navigate to={`${routes?.subscriptionEnd}`} replace />} />
            <Route path={`${routes?.subscriptionEnd}`} element={<SubscriptionEnded />} />
          </Routes>
        </div>
      </Fade>
    )
  }

  return (
    <Fade in timeout={1500}>
      <div>
        {!shouldHideNavigation && (
          <TopBar
            persona={persona}
            togglePersonas={togglePersonas}
            availablePersonas={availablePersonas}
            handleToggleDrawer={handleToggleDrawer}
            landingScreen={landingScreen}
          />
        )}
        {isWarningDisplayed && !shouldHideNavigation && <ExpiryWarning />}
        <div className={styles.root}>
          {!shouldHideNavigation && (
            <Sidebar
              availablePersonas={availablePersonas}
              persona={persona}
              setPersona={setPersona}
              handleToggleDrawer={handleToggleDrawer}
              handleCloseDrawer={handleCloseDrawer}
              isDrawerOpen={isDrawerOpen}
            />
          )}
          <main
            className={styles.content}
            style={{
              padding: location.pathname === `/${routes?.[404]}` ? "0px" : "24px",
              marginTop: isWarningDisplayed ? "100px" : "45px",
            }}
          >
            {Auth.hasFeaturePermission("chatbot") && !shouldHideNavigation && features?.analytics.azka_bot && (
              <Chatbot />
            )}
            <Routes>
              {dashboardRoutes.map((route) => (
                <Route key={route.key} path={route.path} element={getFeatureRouteElement(route, features ?? null)} />
              ))}
            </Routes>
          </main>
        </div>
      </div>
    </Fade>
  )
}

export default Dashboard
